import { i18n } from '@lingui/core';
import { msg, plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import type { BrandAsset } from '@magig/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'motion/react';
import { type ReactNode, useRef, useState } from 'react';
import { toast } from 'sonner';
import { OnboardingBack, OnboardingExit, PageEyebrow } from '../components/auth-shells.js';
import { AssetPreviewModal } from '../components/image-preview-modal.js';
import { Mag } from '../components/mag.js';
import { deleteBrandAsset, uploadBrandAssets } from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { brandAssetsQuery, brandsQuery, meQuery } from '../lib/queries.js';
import { refreshQueries } from '../lib/refresh.js';

// Onboarding step 4 — brand assets review.
//
// What the founder sees:
//   - the images mag auto-fetched from the marketing site during the
//     scrape (logo + product photos + lifestyle stills + hero shots)
//   - per-asset delete button so they can drop misses (random page
//     decoration the classifier didn't quite catch)
//   - upload zone so they can add anything mag missed
//   - continue → /onboarding/competition (the final analysis step)
//
// All UI here reads + writes through the same endpoints the settings
// page uses (/api/brand-brain/assets GET/POST/DELETE). No new backend
// work — this is just a new shell around the existing surface, scoped
// to the onboarding moment so the founder gets a chance to clean up
// before mag starts drafting against the gallery.

const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml', 'image/gif'];

interface OnboardingAssetsSearch {
  onboarding?: '1';
}

export const Route = createFileRoute('/brand/$slug/onboarding/assets')({
  validateSearch: (search: Record<string, unknown>): OnboardingAssetsSearch => {
    if (search['onboarding'] === '1') return { onboarding: '1' };
    return {};
  },
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    // Warm the asset query so the page paints with the gallery
    // already loaded — no skeleton flash for the auto-fetched
    // images that should be the centerpiece on first paint.
    await queryClient.ensureQueryData(brandAssetsQuery(brand.id));
    return { brand };
  },
  component: AssetsStep,
});

function AssetsStep() {
  const { t } = useLingui();

  const { brand } = Route.useRouteContext();
  useBrandTitle(brand.slug, ['onboarding', 'assets']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data } = useQuery(brandAssetsQuery(brand.id));
  const assets = data?.assets ?? [];
  const autoFetched = assets.filter((a) => a.uploadedByUserId === null);
  const founderUploaded = assets.filter((a) => a.uploadedByUserId !== null);

  // 54h.3 — click any tile to open the shared AssetPreviewModal
  // (same chrome as settings + the visual-scrape gallery). state
  // lives here so the modal renders once for the whole page and
  // both galleries route into it.
  const [previewing, setPreviewing] = useState<BrandAsset | null>(null);

  const handleContinue = async () => {
    await navigate({
      to: '/brand/$slug/onboarding/markets',
      params: { slug: brand.slug },
      search: { onboarding: '1' as const },
    });
  };

  return (
    <main className="relative min-h-screen bg-[var(--cream)]">
      <OnboardingBack
        to="/brand/$slug/brief/confirm"
        brandSlug={brand.slug}
        search={{ onboarding: '1' as const }}
      >
        <Trans>back to confirm</Trans>
      </OnboardingBack>
      <OnboardingExit brandSlug={brand.slug} />

      <section className="mx-auto max-w-[820px] px-4 py-12 sm:px-6">
        {/* ── Hero: Mag presents the shelf (matches the brief reveal) ── */}
        <header className="flex flex-col items-start gap-4">
          <div
            className="mag-stagger-in flex items-center gap-3"
            style={{ animationDelay: '40ms' }}
          >
            <div className="relative">
              <div
                aria-hidden="true"
                className="mag-aura pointer-events-none absolute inset-[-14px] rounded-full"
              />
              <div className="mag-breathe relative">
                <Mag size={56} mood="casting" trackCursor />
              </div>
            </div>
            <PageEyebrow>
              <Trans>onboarding · step 4 of 6 · your shelf</Trans>
            </PageEyebrow>
          </div>
          <h1
            className="mag-stagger-in font-display text-[40px] sm:text-[60px] font-black leading-[0.98] tracking-[-0.045em]"
            style={{ animationDelay: '140ms' }}
          >
            <Trans>
              here's the visual world
              <br />
              <span className="text-[var(--plum)]">i'll design with.</span>
            </Trans>
          </h1>
          <p
            className="mag-stagger-in max-w-[540px] font-serif text-xl leading-tight text-[var(--ink)]/80"
            style={{ animationDelay: '220ms' }}
          >
            <Trans>
              “everything below is what i pulled off your site. tap to drop anything that isn't
              yours — or add what i missed.”
            </Trans>
          </p>
        </header>

        {/* ── Centerpiece: what Mag pulled ── */}
        <div className="mag-stagger-in mt-10" style={{ animationDelay: '320ms' }}>
          <AutoFetchedGallery
            brand={brand}
            assets={autoFetched}
            queryClient={queryClient}
            onOpen={setPreviewing}
          />
        </div>

        {/* ── Quiet affordances: reference + upload, tucked away ── */}
        <div
          className="mag-stagger-in mt-4 flex flex-col gap-4"
          style={{ animationDelay: '420ms' }}
        >
          <RevealPanel label={t`what i use these for`}>
            <AssetUsageExplainer />
          </RevealPanel>

          <RevealPanel label={t`add your own images`} defaultOpen={autoFetched.length === 0}>
            <div className="flex flex-col gap-4">
              <UploadDropzone brandId={brand.id} queryClient={queryClient} />
              {founderUploaded.length > 0 ? (
                <FounderUploadedGallery
                  brand={brand}
                  assets={founderUploaded}
                  queryClient={queryClient}
                  onOpen={setPreviewing}
                />
              ) : null}
            </div>
          </RevealPanel>
        </div>

        <div
          className="mag-stagger-in mt-10 flex items-center justify-end gap-3"
          style={{ animationDelay: '520ms' }}
        >
          <button type="button" onClick={handleContinue} className="btn btn-lg">
            <Trans>next · markets</Trans>
            <span className="btn-arrow">→</span>
          </button>
        </div>
      </section>

      <AnimatePresence>
        {previewing ? (
          <AssetPreviewModal asset={previewing} onClose={() => setPreviewing(null)} />
        ) : null}
      </AnimatePresence>
    </main>
  );
}

// ─── reveal panel (quiet collapsible) ─────────────────────────────
// Reference copy + the upload form are tucked behind these so the
// shelf (what Mag pulled) stays the centerpiece — info/forms appear
// only when the founder reaches for them.

function RevealPanel({
  label,
  defaultOpen = false,
  children,
}: {
  label: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 rounded-xl border border-[var(--ink)]/12 bg-white/50 px-4 py-3 text-left transition hover:border-[var(--ink)]/30"
      >
        <span className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          ↘ {label}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/45">
          {open ? <Trans>× hide</Trans> : <Trans>open ↓</Trans>}
        </span>
      </button>
      {open ? <div className="mag-fade-in mt-3">{children}</div> : null}
    </div>
  );
}

// ─── auto-fetched gallery ──────────────────────────────────────────

function AutoFetchedGallery({
  brand,
  assets,
  queryClient,
  onOpen,
}: {
  brand: { id: string };
  assets: BrandAsset[];
  queryClient: ReturnType<typeof useQueryClient>;
  onOpen: (asset: BrandAsset) => void;
}) {
  // Default to a single row (4 thumbs on md+; 2 on phone). Anything
  // beyond gets revealed by the "show all" toggle. Galleries can run
  // 30+ images when a brand has rich photography on their site —
  // dumping the whole grid up-front made the upload zone scroll off
  // the bottom of the viewport.
  const PREVIEW_COUNT = 4;
  const [expanded, setExpanded] = useState(false);
  const overflowCount = Math.max(0, assets.length - PREVIEW_COUNT);
  const visible = expanded ? assets : assets.slice(0, PREVIEW_COUNT);

  if (assets.length === 0) {
    return (
      <div className="rounded-2xl border-[1.5px] border-dashed border-[var(--ink)]/25 bg-white p-6 text-center">
        <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
          {'↘'} <Trans>what i pulled · 0</Trans>
        </p>
        <p className="mt-3 mag-voice text-[14px] leading-[1.5] text-[var(--ink)]/65">
          <Trans>
            “your site didn't have many images for me to grab. upload some below and i'll be set.”
          </Trans>
        </p>
      </div>
    );
  }
  return (
    <section className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
        <Trans>↘ what i pulled · {assets.length}</Trans>
      </p>
      <p className="mt-2 text-[13px] leading-[1.5] text-[var(--ink)]/70">
        <Trans>tap an image to remove anything that isn't yours.</Trans>
      </p>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
        {/* popLayout — surrounding tiles start sliding into the gap
            the instant a tile begins its exit, rather than waiting
            for exit to finish. matches the "list adjusts to empty
            space" expectation. */}
        <AnimatePresence mode="popLayout">
          {visible.map((asset) => (
            <AssetTile
              key={asset.id}
              asset={asset}
              brand={brand}
              queryClient={queryClient}
              onOpen={onOpen}
            />
          ))}
        </AnimatePresence>
      </div>
      {overflowCount > 0 ? (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="btn btn-ghost btn-sm"
          >
            {expanded ? `↑ collapse` : `↓ show all ${assets.length}`}
          </button>
        </div>
      ) : null}
    </section>
  );
}

/** 54d/N — "what mag uses these for" explainer. Sits between the
 *  intro and the gallery so the founder reads the why before they
 *  start curating. Lists each kind with one mag-voice line about
 *  what it gets used for, plus a logo-variants hint that's the
 *  highest-leverage upload (most brands ship one logo file; mag
 *  needs three for light/dark/icon use cases). */
function AssetUsageExplainer() {
  return (
    <section className="rounded-2xl border border-[var(--ink)]/15 bg-[var(--paper)] p-5 sm:p-6">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
        {'↘'} <Trans>what i use these for</Trans>
      </p>
      <ul className="mt-3 flex flex-col gap-2.5 text-[13px] leading-[1.5] text-[var(--ink)]/80">
        <li>
          <Trans>
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/65">
              logo
            </span>{' '}
            — the brand mark in every render. ideally upload three: the <b>full logo</b> for light
            backgrounds, the <b>iso / mark-only</b> for tight corners + favicon spots, and a{' '}
            <b>white version</b> so dark hero sections don't swallow it.
          </Trans>
        </li>
        <li>
          <Trans>
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/65">
              product photo
            </span>{' '}
            — the thing you sell. mag picks one of these for any concept that names a product;
            without them she falls back to stock-ish renders that don't read as yours.
          </Trans>
        </li>
        <li>
          <Trans>
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/65">
              lifestyle photo
            </span>{' '}
            — people, scenes, context. used on testimonial cards + emotional hooks where a clean
            product shot reads cold.
          </Trans>
        </li>
        <li>
          <Trans>
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/65">
              illustration
            </span>{' '}
            — icons, drawings, abstract shapes. mag layers these onto carousels + steps-list
            templates when a photo would compete with the headline.
          </Trans>
        </li>
      </ul>
    </section>
  );
}

// ─── upload dropzone ───────────────────────────────────────────────

function UploadDropzone({
  brandId,
  queryClient,
}: {
  brandId: string;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  // Per-file failures surface inline below the dropzone so the founder
  // sees exactly WHY a file was rejected ("logo + jpeg" is the most
  // common — the mime allowlist only accepts png + svg for logos).
  const [failures, setFailures] = useState<Array<{ filename: string; reason: string }>>([]);
  const mutation = useMutation({
    mutationFn: async (files: File[]) =>
      // Kind is auto-discovered per file by a vision model (s216-54h.90);
      // we still tag every founder upload 'onboarding' for provenance.
      uploadBrandAssets(brandId, files, undefined, ['onboarding']),
    onSuccess: async (result) => {
      await refreshQueries(queryClient, ['brand-assets', brandId]);
      const okCount = result.assets.length;
      const failCount = result.failed.length;
      setFailures(result.failed);
      if (okCount > 0) {
        toast.success(
          plural(okCount, {
            one: 'added # asset to your shelf.',
            other: 'added # assets to your shelf.',
          }),
        );
      }
      if (failCount > 0) {
        toast.error(
          plural(failCount, {
            one: "# couldn't be uploaded — see why below.",
            other: "# couldn't be uploaded — see why below.",
          }),
        );
      }
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const handleFiles = (fileList: FileList | File[]) => {
    const files = Array.from(fileList).filter((f) => f.size > 0);
    if (files.length === 0) return;
    setFailures([]);
    mutation.mutate(files);
  };

  // Accepted formats (mirrors ASSET_MIME_ALLOWLIST on the api).
  const acceptedHint = 'jpeg · png · webp · svg · gif';

  return (
    <section className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--plum)] sm:p-6">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
        {'↘'} <Trans>add more</Trans>
      </p>
      <p className="mt-2 text-[13px] leading-[1.5] text-[var(--ink)]/70">
        <Trans>
          drag images in or pick from your computer. mag figures out what each one is — logo,
          product shot, lifestyle — automatically.
        </Trans>
      </p>

      {/* biome-ignore lint/a11y/noStaticElementInteractions: drag/drop zone with file-input fallback — file input is keyboard-focusable */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={`mt-4 rounded-2xl border-[1.5px] border-dashed p-8 text-center transition ${
          dragging
            ? 'border-[var(--plum)] bg-[var(--paper)]'
            : 'border-[var(--ink)]/30 bg-[var(--paper)]/60'
        }`}
      >
        {mutation.isPending ? (
          <p className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>uploading…</Trans>
          </p>
        ) : (
          <>
            <p className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
              <Trans>drop files here</Trans>
            </p>
            <p className="mt-2 text-[13px] text-[var(--ink)]/65">
              <Trans>{acceptedHint} — up to 8mb each</Trans>
            </p>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="btn btn-spell mt-4"
            >
              <Trans>pick files</Trans>
              <span className="btn-arrow">→</span>
            </button>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_MIME_TYPES.join(',')}
          multiple
          onChange={(e) => {
            if (e.target.files) handleFiles(e.target.files);
            if (inputRef.current) inputRef.current.value = '';
          }}
          className="hidden"
        />
      </div>

      {failures.length > 0 ? (
        <div className="mt-4 rounded-xl border border-[var(--pulse)]/40 bg-[var(--pulse)]/5 p-4">
          <div className="flex items-baseline justify-between gap-3">
            <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[var(--pulse)]">
              ↘ {failures.length}{' '}
              {failures.length === 1 ? <Trans>didn't land</Trans> : <Trans>didn't land</Trans>}
            </p>
            <button
              type="button"
              onClick={() => setFailures([])}
              className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/55 hover:text-[var(--ink)]"
            >
              <Trans>dismiss</Trans>
            </button>
          </div>
          <ul className="mt-2 flex flex-col gap-1.5">
            {failures.map((f) => (
              <li
                key={`${f.filename}-${f.reason}`}
                className="flex items-baseline justify-between gap-3 text-[12.5px] leading-[1.4]"
              >
                <span className="truncate font-mono text-[11px] text-[var(--ink)]/80">
                  {f.filename}
                </span>
                <span className="text-right text-[var(--ink)]/65">{f.reason}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 mag-voice text-[12.5px] leading-[1.45] text-[var(--ink)]/65">
            <Trans>
              “usually a too-large file or an unusual format — for logos a transparent png or svg
              lands cleanest. resave + try again.”
            </Trans>
          </p>
        </div>
      ) : null}
    </section>
  );
}

// ─── founder-uploaded gallery ──────────────────────────────────────

function FounderUploadedGallery({
  brand,
  assets,
  queryClient,
  onOpen,
}: {
  brand: { id: string };
  assets: BrandAsset[];
  queryClient: ReturnType<typeof useQueryClient>;
  onOpen: (asset: BrandAsset) => void;
}) {
  return (
    <section className="rounded-2xl border-[1.5px] border-[var(--ink)]/15 bg-[var(--paper)] p-5 sm:p-6">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
        <Trans>↘ what you added · {assets.length}</Trans>
      </p>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
        <AnimatePresence mode="popLayout">
          {assets.map((asset) => (
            <AssetTile
              key={asset.id}
              asset={asset}
              brand={brand}
              queryClient={queryClient}
              onOpen={onOpen}
            />
          ))}
        </AnimatePresence>
      </div>
    </section>
  );
}

// ─── asset tile (shared) ──────────────────────────────────────────

function AssetTile({
  asset,
  queryClient,
  onOpen,
}: {
  asset: BrandAsset;
  brand: { id: string };
  queryClient: ReturnType<typeof useQueryClient>;
  onOpen: (asset: BrandAsset) => void;
}) {
  const { t } = useLingui();

  // 54g/N — two-step inline confirm replaces the native confirm()
  // popup. first click on "remove ×" arms the tile; second click on
  // "drop" actually deletes. cancel reverts. auto-reverts after 4s
  // so an accidental arm doesn't sit forever blocking the tile.
  //
  // 54h.2/N — animation orchestration is now declarative via motion:
  //   - AnimatePresence (in the parent gallery) detects unmount when
  //     the asset disappears from the refetched list
  //   - this motion.div's `exit` prop runs the drop-out
  //   - this motion.div's `layout` prop animates the SIBLINGS' grid
  //     positions when this tile leaves (or any other does)
  //
  // sequence on confirm:
  //   1. api call fires
  //   2. on success → setDropped(true) (200ms ✓ flash overlay)
  //   3. refreshQueries — tile disappears from the list
  //   4. AnimatePresence + motion run exit + sibling layout in parallel
  const [armed, setArmed] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dropped, setDropped] = useState(false);

  // Auto-revert the armed state. uses an effect-less timeout that
  // clears when armed flips back to false (manual cancel or success).
  // Inline rather than useEffect because the gallery may have 30+
  // tiles and an effect-per-tile would churn renders.
  const arm = () => {
    setArmed(true);
    window.setTimeout(() => {
      // best-effort — if the founder already confirmed/canceled by
      // the time this fires, the setter is a no-op.
      setArmed(false);
    }, 4_000);
  };

  const confirmDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await deleteBrandAsset(asset.id);
      // Show the ✓ flash for 200ms so the founder sees the commit
      // land, then refetch. The tile disappears from the list →
      // AnimatePresence + motion handle exit + sibling reflow.
      setDropped(true);
      window.setTimeout(() => {
        void refreshQueries(queryClient, ['brand-assets']);
      }, 200);
      toast.success(i18n._(msg`removed.`));
    } catch (err) {
      toast.error(founderErrorCopy(err, 'try again'));
      setDeleting(false);
      setArmed(false);
    }
  };

  const preview = asset.thumbnailUrl ?? asset.url;
  return (
    <motion.div
      // layout — tracks this tile's bounding rect across re-renders
      // and animates from old → new grid slot when siblings come or
      // go. exit — runs the drop-out when this tile is removed from
      // the list (drives off AnimatePresence in the parent gallery).
      layout
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.7, rotate: -3 }}
      transition={{
        layout: { duration: 0.32, ease: [0.22, 1, 0.36, 1] },
        opacity: { duration: 0.32, ease: [0.55, 0.06, 0.68, 0.19] },
        scale: { duration: 0.32, ease: [0.55, 0.06, 0.68, 0.19] },
        rotate: { duration: 0.32, ease: [0.55, 0.06, 0.68, 0.19] },
      }}
      className="group relative overflow-hidden rounded-xl border border-[var(--ink)]/15 bg-white"
    >
      {/* Image is the click target for preview. Overlays (armed
          confirm, dropped flash) sit absolutely on top and intercept
          clicks during those states — so this button is only
          reachable when the tile is in its idle hover state. */}
      <button
        type="button"
        onClick={() => onOpen(asset)}
        aria-label={t`preview ${asset.filename}`}
        className="block w-full cursor-zoom-in"
      >
        <img
          src={preview}
          alt={asset.filename}
          className="block aspect-square w-full object-cover transition group-hover:opacity-95"
        />
      </button>

      {dropped ? (
        // 54g.3/N — success flash. shows for 200ms between the api
        // success and the tile fall. plum-tinted overlay + a big
        // checkmark + "removed" copy so the founder sees the
        // commit land before the visual collapses.
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-[var(--plum)]/12 text-center"
          style={{ backdropFilter: 'blur(1px)' }}
        >
          <span className="mag-success-pop inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--plum)] font-mono text-[20px] font-bold text-[var(--cream)]">
            ✓
          </span>
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
            <Trans>removed</Trans>
          </p>
        </div>
      ) : armed ? (
        // 54g.2/N — stacked vertically so the brutalist .btn shadows
        // (2-3px hard offset on .btn-sm) get the breathing room they
        // need inside a ~195px tile. side-by-side clipped the right-
        // edge shadow of the confirm button against the tile's
        // overflow-hidden mask. light overlay + 2px blur keeps the
        // image visible while pushing the buttons forward.
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-2.5 bg-[var(--paper)]/92 px-4 py-3 text-center"
          style={{
            animation: 'mag-fade-in 180ms ease-out both',
            backdropFilter: 'blur(2px)',
          }}
        >
          {/* The overlay's mag-fade-in carries this content with it.
              We don't put per-element animations on the .btn here
              because animation fill-mode locks the transform — which
              then overrides the .btn's :hover translate. one
              animation, on the wrapper, lets the brand button motion
              vocabulary take over for hover/active naturally. */}
          <p className="mag-voice text-[13px] leading-tight text-[var(--ink)]/85">
            <Trans>“drop this one?”</Trans>
          </p>
          <button
            type="button"
            onClick={confirmDelete}
            disabled={deleting}
            className="btn btn-pulse-solid btn-sm disabled:cursor-not-allowed disabled:opacity-50"
          >
            {deleting ? <Trans>dropping…</Trans> : <Trans>drop</Trans>}
          </button>
          <button
            type="button"
            onClick={() => setArmed(false)}
            disabled={deleting}
            className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/55 transition hover:text-[var(--ink)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Trans>cancel</Trans>
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={arm}
          aria-label={t`remove asset`}
          className="absolute right-1.5 top-1.5 rounded-full border border-[var(--ink)] bg-white px-2 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)] opacity-0 transition group-hover:opacity-100 hover:bg-[var(--pulse)] hover:text-white"
        >
          <Trans>remove ×</Trans>
        </button>
      )}

      <div className="px-2 py-1.5">
        <p className="truncate font-mono text-[9.5px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          {asset.kind.replace(/_/g, ' ')}
        </p>
      </div>
    </motion.div>
  );
}
